import React from 'react';
import * as immutable from 'immutable';
import PropTypes from 'prop-types';
import connectToStores from 'alt-utils/lib/connectToStores';
import pick from 'lodash/pick';
import AdminTopicEditorPageStore from 'stores/admin/AdminTopicEditorPageStore';
import NewFilterWord from 'records/admin/topics/NewFilterWord';
import TopicFormWords from 'components/admin/topics/TopicFormWords';
import TopicFormUsage from 'components/admin/topics/TopicFormUsage';
import TopicFormHeader from 'components/admin/topics/TopicFormHeader';
import './TopicForm.styl';
import Button from '@material-ui/core/Button';
import confirm from 'utils/confirm';
import {goToTopicsAdminPage} from 'components/admin/topics/common';
import AdminFilterWordProfileActions from 'actions/admin/AdminFilterWordProfileActions';
import ToastStore from '@store/toast/ToastStore';
import AdminTopicEditorPageActions from 'actions/admin/AdminTopicEditorPageActions';

/**
 * The topic editor form.
 */
@connectToStores
export default class TopicForm extends React.PureComponent {
  static propTypes = {
    alternateSpellings: PropTypes.instanceOf(immutable.List),
    editable: PropTypes.bool.isRequired,
    idToProfileMap: PropTypes.instanceOf(immutable.Map).isRequired,
    profileDepartmentAndTeamUsage: PropTypes.instanceOf(immutable.Map).isRequired,
    topic: PropTypes.instanceOf(NewFilterWord),
    triggers: PropTypes.instanceOf(immutable.List).isRequired,
    wordsToTopics: PropTypes.instanceOf(immutable.Map).isRequired,
  };

  static getStores() {
    return [AdminTopicEditorPageStore];
  }

  static getPropsFromStores() {
    return pick(AdminTopicEditorPageStore.getState(), [
      'alternateSpellings',
      'editingNewTopic',
      'editingTopicName',
      'topic',
      'topicName',
      'showErrors',
    ]);
  }

  componentDidMount() {
    AdminTopicEditorPageActions.hideErrors.defer();
  }

  cancel() {
    const {editingNewTopic} = this.props;
    if (editingNewTopic) {
      confirm('Are you sure you want to cancel creation of this topic?').then(goToTopicsAdminPage);
    } else {
      goToTopicsAdminPage();
    }
  }

  save() {
    // Make sure we've got the updated alts. Kinda janky, but whatever.
    const {alternateSpellings, topic} = AdminTopicEditorPageStore.getState();
    if (topic.includeInInsights === null) {
      return AdminTopicEditorPageActions.showErrors();
    }
    // group by and grab the first, minor, but this way we'll preserve the caps
    // of at least one of them
    const newTopic = topic.set(
      'alternateSpellings',
      alternateSpellings
        .filter(a => !!a && a.trim() !== '')
        .groupBy(w => w.toLowerCase())
        .map(w => w.get(0))
        .toOrderedSet()
    );
    const {editingNewTopic} = this.props;
    const saveAction = editingNewTopic
      ? AdminFilterWordProfileActions.createFilterWord
      : AdminFilterWordProfileActions.updateFilterWord;
    saveAction(newTopic).then(
      () => this.saveSuccess(alternateSpellings),
      e => this.saveError(e)
    );
  }

  saveError(e) {
    console.error(e);
    ToastStore.addToast({
      type: 'error',
      message: 'There was a error communicating with the server.',
    });
  }

  saveSuccess(words) {
    const dupes = words.groupBy(w => w.toLowerCase()).filter(w => w.size > 1);

    let cleanUpMessage = '';
    if (dupes.size > 0) {
      cleanUpMessage = `Note: Cleaned up ${
        dupes.reduce((a, b) => a + b.size, 0) - dupes.size
      } duplicate word / phrases.`;
    }

    // editor state is reset when container unmounts, so that's handled for us here.
    goToTopicsAdminPage();
    ToastStore.addToast({
      message: 'Changes saved.' + (cleanUpMessage ? '  ' + cleanUpMessage : ''),
    });
  }

  render() {
    const {
      alternateSpellings,
      editable,
      editingNewTopic,
      editingTopicName,
      idToProfileMap,
      profileDepartmentAndTeamUsage,
      topic,
      topicName,
      triggers,
      wordsToTopics,
      showErrors,
    } = this.props;

    const invalidTopicName = !topicName || topicName.trim() === '';

    return (
      <div className="admin-topic-form">
        <TopicFormHeader
          editable={editable}
          editingNewTopic={editingNewTopic}
          editingTopicName={editingTopicName}
          idToProfileMap={idToProfileMap}
          profileDepartmentAndTeamUsage={profileDepartmentAndTeamUsage}
          topic={topic}
          topicName={topicName}
          triggers={triggers}
          showErrors={showErrors}
        />
        <div className="admin-topic-form-body">
          <div className="main-content">
            <TopicFormWords
              alternateSpellings={alternateSpellings}
              editable={editable}
              editingNewTopic={editingNewTopic}
              topic={topic}
              wordsToTopics={wordsToTopics}
            />
            {editable && (
              <div className="admin-topic-form-buttons">
                <Button
                  variant="contained"
                  color="primary"
                  className="ev-button"
                  onClick={() => this.save()}
                  disabled={invalidTopicName || editingTopicName}>
                  Save
                </Button>
                <Button variant="outlined" className="ev-button" onClick={() => this.cancel()}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
          <TopicFormUsage
            idToProfileMap={idToProfileMap}
            profileDepartmentAndTeamUsage={profileDepartmentAndTeamUsage}
            topic={topic}
            triggers={triggers}
          />
        </div>
      </div>
    );
  }
}
